<template>
  <div class="vz-special-requirement-roadmap">
    <div class="vz-special-requirement-roadmap-description mb-5">
      Максимальный размер файла: <span class="text-bold">5 МБ</span>
    </div>
    <div class="vz-special-requirement-roadmap-description mb-20">
      Поддерживаемые форматы: <span class="text-bold">tif, tiff, png, jpg, jpeg, pdf</span>
      <vz-tooltip-info text="Файлы PDF формата должны быть одностраничными." />
    </div>
    <div v-if="fileName" class="vz-special-requirement-roadmap-file flex flex-space-between mb-10">
      <div class="vz-special-requirement-roadmap-wrapper flex">
        <vz-icon
          size=24
          no-masked-icon
          variant="primary"
          url="/svg/vz-documents.svg"
        />
        <div class="vz-special-requirement-roadmap-file-data ml-10 flex flex-column flex-center">
          <div>{{ fileName }}</div>
          <div>{{ fileSize }}</div>
        </div>
      </div>
      <div class="vz-special-requirement-roadmap-wrapper flex ml-10">
        <a
          download
          target="_blank"
          class="mr-20 flex flex-align-items-center vz-special-requirement-roadmap-download vz-cursor-pointer"
          :href="props.value"
          @click="downloadFile"
        >
          <vz-icon 
            size=14
            no-masked-icon
            url="/svg/vz-download.svg"
          />
        </a>
        <vz-icon
          size=16
          name="delete"
          class="vz-cursor-pointer"
          @click="remove"
        />
      </div>
    </div>
    <vz-file-uploader
      v-model="files"
      large
      outline
      variant="primary"
      show-upload-problems
      upload-problems-with-icon
      btn-title="Загрузить файл"
      icon-name="paperclip"
      icon-size="16"
      :max-files=1
      :size="5242880"
      :accept="['tif', 'tiff', 'png', 'jpg', 'jpeg' , 'pdf']"
      :disabled="disabled"
      @change="upload"
    />
  </div>
</template>

<script setup lang="ts">
import VzFileUploader from "@/components/ui-kit/vz-file-uploader.vue"
import { useOrderStore } from "@/stores/order"
import { getOrderDocument } from "@/api/document"
import {uploadFile} from '~/api/backend';
import { FileUploadProblems } from "~/types/types";

const  emit = defineEmits(['update:value', 'update:active'])

const props = defineProps({
  value: {
    type: String,
    default: ''
  },
  active: {
    type: Boolean,
    default: false
  },
  oldActive: {
    type: Boolean,
    default: false
  },
  directionTo: {
    type: Boolean,
    default: false
  }
})

const orderStore = useOrderStore()

const files = ref<File[]>([])

const fileSize = computed(() => {
  const s = files.value[0] && files.value[0].size > 0 ? (files.value[0].size/Math.pow(1024, 2)).toFixed(2) : ''
  return s.length > 0 && !props.value.length ? `${s} МБ` : ''
})

const fileName = computed(() => {
  return !!props.value.length 
    ? props.value.split('/').pop()
    : (
        files.value[0]
        ? files.value[0].name
        : (
            props.oldActive
            ? 'Схема проезда'
            : ''
        )
      )
})

const disabled = computed(() => {
  return !!props.value.length || !!files.value[0] || props.oldActive
})

function remove():void {
  emit('update:active', false)
}

async function upload(f: File[], uploadProblems: FileUploadProblems) {
  if (!uploadProblems.accept && !uploadProblems.size) {
    const formData = new FormData()
    formData.append('file', files.value[0])
    formData.append('type', 'roadmap')

    const { url } = await uploadFile(formData)
    if (url) {
      emit('update:value', url)
      files.value.pop()
    }
  }
}

// загрузка файла вызывается только в режиме редактирования заказа, по нажатию пользователя
async function downloadFile() {
  if (!props.value.length && orderStore.orderForm.guid) {
    const direction = props.directionTo ? 'destination' : 'dispatch'
    const { documentUrl } = await getOrderDocument({ orderGuid: orderStore.orderForm.guid, docType: 'roadmap', gateType: direction })
    if (documentUrl) {
      window.open(documentUrl)
    }
  }
}
</script>

<style lang="scss" scoped>
.vz-special-requirement-roadmap {
  &-description {
    font-size: $fs-14;
    color: $font-light;
  }

  &-file {
    background: $box-gray;
    border-radius: 4px;
    padding: 10px 16px 10px 12px;
    font-size: 12px;
    line-height: 16px;
    min-height: 52px;

    &-data {
      div {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        max-width: 230px;
        width: 100%;

        @media screen and (max-width: $pa-mdm) {
          max-width: 180px;
        }

        @media screen and (max-width: $pa-smm) {
          max-width: 110px;
        }

        @media screen and (max-width: $pa-sm) {
          max-width: 180px;
        }

        @media screen and (max-width: $pa-xs) {
          max-width: 80px;
        }
      }
    }
  }

  :deep(.vz-file-uploader-new) {
    overflow: hidden;

    .vz-button {
      width: 100%;
      justify-content: center;
      border-color: $border-gray !important;
      height: 52px;

      &:hover:not(:disabled) {
        border-color: $primary !important;
      }

      &-title {
        font-size: $fs-14;
      }

      .vz-icon {
        transform: rotate(45deg);
      }
    }
  }
}
</style>
